import { RUNS_ON_ATLASSIAN_PROGRAM } from '@atlassian/mpac-types';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { log, UrlUtils } from '@atlassian/mpac-utils';
import { CommunityTagForHostingType } from './constants';
export const COMMUNITY_CATEGORY_ID_MARKETPLACE = 'atlassian-marketplace';
export const APP_HEADER_ORIGIN = 'marketplace';
export const makeAddonKey = appKey => "addon-".concat(appKey);

/**
 * Get the URL for viewing existing questions on Atlassian Community for a given appKey.
 *
 * @example makeSeeExistingQuestionsUrl({
 *  atlassianCommunityBaseUrl: 'https://community.atlassian.com',
 *  appKey: 'is.origo.jira.tempo-plugin'
 * }) => 'https://community.atlassian.com/t5/tag/addon-is.origo.jira.tempo-plugin/tg-p/category-id/atlassian-marketplace'
 */
export const makeSeeExistingQuestionsUrl = _ref => {
  let {
    atlassianCommunityBaseUrl,
    appKey
  } = _ref;
  return "".concat(atlassianCommunityBaseUrl, "/t5/tag/").concat(makeAddonKey(appKey), "/tg-p/category-id/").concat(COMMUNITY_CATEGORY_ID_MARKETPLACE);
};

/**
 * Get the URL for viewing the "add new question" experience on
 * Atlassian Community for a given appKey & hosting.
 *
 * @example makeSeeExistingQuestionsUrl({
 *  atlassianCommunityBaseUrl: 'https://community.atlassian.com',
 *  appKey: 'is.origo.jira.tempo-plugin',
 *  hosting: 'CLOUD'
 * }) => 'https://community.atlassian.com/t5/custom/page/page-id/create-post-step-1?add-tags=atlassian-marketplace,addon-is.origo.jira.tempo-plugin,cloud'
 */
export const makeAskNewQuestionUrl = _ref2 => {
  let {
    atlassianCommunityBaseUrl,
    appKey,
    hosting
  } = _ref2;
  const pathname = "".concat(atlassianCommunityBaseUrl, "/t5/custom/page/page-id/create-post-step-1");
  const params = new URLSearchParams();
  params.append('add-tags', [COMMUNITY_CATEGORY_ID_MARKETPLACE, makeAddonKey(appKey), CommunityTagForHostingType[hosting]].join(','));
  return "".concat(pathname, "?").concat(params.toString());
};

/**
 * Get the MPAC REST API URL for querying how many existing questions are there
 * for a given appKey on Atlassian Community
 */
export const makeGetExistingQuestionCountUrl = _ref3 => {
  let {
    mpacBaseUrl,
    appKey
  } = _ref3;
  return "".concat(mpacBaseUrl, "/rest/internal/community/tags/count?q=").concat(makeAddonKey(appKey));
};
export const getExistingCommunityQuestionsCount = async _ref4 => {
  let {
    mpacBaseUrl,
    appKey
  } = _ref4;
  try {
    const url = makeGetExistingQuestionCountUrl({
      mpacBaseUrl,
      appKey
    });
    const response = await fetch(url, {
      credentials: 'same-origin'
    });
    if (response.ok) {
      const count = await response.json();
      if (typeof count === 'number') return count;
    }
  } catch (error) {
    log.error('AppListing', 'Error fetching count of existing questions on atlassian community', {
      error
    });
  }
  return 0;
};
export const valueForHosting = hosting => hostingMap => {
  return hostingMap[hosting];
};
export const valueForPaymentModel = paymentModel => paymentModelMap => {
  return paymentModelMap[paymentModel];
};
export const valueForPurchaseIntent = purchaseIntent => purchaseIntentMap => {
  return purchaseIntentMap[purchaseIntent];
};
export function getSelectedAppVersion(hosting, appDetails) {
  var _getSelectedAppVersio, _getSelectedAppVersio2;
  return (_getSelectedAppVersio = getSelectedAppVersionConnection(hosting, appDetails).edges) === null || _getSelectedAppVersio === void 0 ? void 0 : (_getSelectedAppVersio2 = _getSelectedAppVersio[0]) === null || _getSelectedAppVersio2 === void 0 ? void 0 : _getSelectedAppVersio2.node;
}
export function getSelectedAppVersionConnection(hosting, appDetails) {
  return valueForHosting(hosting)({
    [AtlassianProductHostingType.Cloud]: appDetails.cloudAppVersions,
    [AtlassianProductHostingType.Server]: appDetails.serverAppVersions,
    [AtlassianProductHostingType.DataCenter]: appDetails.dataCenterAppVersions
  });
}
export const makeAppHeaderProps = (appListing, appDetailsFromSearchResponse) => {
  var _appListing$tags, _appListing$tags$mark;
  return {
    name: appListing.name,
    key: appListing.appKey,
    ratings: {
      avgStars: appListing.reviewSummary.rating,
      numRatings: appListing.reviewSummary.count
    },
    createdAt: appListing.createdAt,
    distribution: {
      downloadCount: appListing.distribution.downloadCount,
      installationCount: appListing.distribution.installationCount,
      isPreinstalled: appListing.distribution.isPreinstalled,
      isPreinstalledInAnyHosting: appListing.distribution.isPreinstalledInAnyHosting,
      isLoading: appDetailsFromSearchResponse === null || appDetailsFromSearchResponse === void 0 ? void 0 : appDetailsFromSearchResponse.loading,
      isError: appDetailsFromSearchResponse === null || appDetailsFromSearchResponse === void 0 ? void 0 : appDetailsFromSearchResponse.error
    },
    compatibleProducts: appListing.compatibleProducts.map(p => p.name),
    origin: APP_HEADER_ORIGIN,
    partner: {
      name: appListing.partner.name,
      link: UrlUtils.validateSafeAbsoluteUrl(appListing.partner.url),
      partnerTierType: appListing.partner.partnerTierType
    },
    programs: {
      runsOnAtlassian: {
        status: (appListing === null || appListing === void 0 ? void 0 : (_appListing$tags = appListing.tags) === null || _appListing$tags === void 0 ? void 0 : (_appListing$tags$mark = _appListing$tags.marketingTags) === null || _appListing$tags$mark === void 0 ? void 0 : _appListing$tags$mark.some(tag => tag.name === RUNS_ON_ATLASSIAN_PROGRAM)) || false
      }
    }
  };
};
export const isRuByPartnerAppAccessDeniedError = error => {
  var _graphQLErrors$0$exte;
  const {
    graphQLErrors
  } = error;
  if (!graphQLErrors.length) return false;
  return ((_graphQLErrors$0$exte = graphQLErrors[0].extensions) === null || _graphQLErrors$0$exte === void 0 ? void 0 : _graphQLErrors$0$exte.errorType) === 'ruby_region_access_denied';
};